import moment from 'moment';
import { useCallback } from 'react';

const { BlobServiceClient } = require('@azure/storage-blob');

interface FileService {
  uploadFileToBlobStorage: (file: File) => Promise<BlobFile>;
  getBlobUrlWithSas: (fileName: string) => string;
}

export interface BlobFile {
  blobName: string;
  imageurl: string;
}

interface Props {
  sasUriData: SasUriData;
}

interface SasUriData {
  account: string;
  sasUri: string;
  containerName: string;
}

const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please refresh the page and try uploading again.';

const isAuthenticationError = (error: any): boolean => {
  if (!error) return false;
  const statusCode = error.statusCode ?? error.response?.status;
  const code = error.code || error.details?.code;
  const message: string = error.message || '';
  return (
    statusCode === 403 ||
    code === 'AuthenticationFailed' ||
    code === 'AuthorizationFailure' ||
    /server failed to authenticate/i.test(message)
  );
};

const useBlobFileService = (sasUriData: SasUriData): FileService => {
  const uploadFileToBlobStorage = useCallback(
    async (file: File): Promise<BlobFile> => {
      const { account, sasUri, containerName } = sasUriData;
      const fileType = file.type;
      const tempArr = file.name.split('.');
      const blobName = `${tempArr[0]}_${moment().unix()}.${tempArr[tempArr.length - 1]}`;
      const blobServiceClient = new BlobServiceClient(
        `https://${account}.blob.core.windows.net/?${sasUri}`
      );
      const containerClient = blobServiceClient.getContainerClient(containerName);
      containerClient.getBlockBlobClient(blobName);
      const blobClient = containerClient.getBlockBlobClient(blobName);
      // set mimetype as determined from browser with file upload control
      const options = { blobHTTPHeaders: { blobContentType: fileType } };
      let uploadResponse;
      try {
        uploadResponse = await blobClient.uploadData(file, options);
      } catch (error) {
        if (isAuthenticationError(error)) {
          throw new Error(SESSION_EXPIRED_MESSAGE);
        }
        throw error;
      }
      if (uploadResponse._response.status === 403) {
        throw new Error(SESSION_EXPIRED_MESSAGE);
      }
      if (uploadResponse._response.status !== 201 || !uploadResponse.etag) {
        throw new Error('File upload to storage failed. Please try again.');
      }
      return { blobName, imageurl: blobClient.url };
    },
    [sasUriData]
  );

  const getBlobUrlWithSas = useCallback(
    (fileName: string): string => {
      const { account, sasUri, containerName } = sasUriData;
      const blobUrl = `${fileName}?${sasUri}`;
      return blobUrl;
    },
    [sasUriData]
  );

  return { uploadFileToBlobStorage, getBlobUrlWithSas };
};

export default useBlobFileService;
